import { useEffect, useState } from "react";
import { Mail, Loader2, Send, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * SMTP status + test-send card shown to admins only.
 * The SMTP credentials themselves are managed via backend env vars (never
 * exposed to the frontend) — this card is purely for verification.
 */
export default function SmtpSettingsCard() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email/config");
      setConfig(data);
    } catch (e) {
      console.warn("[smtp] failed to load config:", e);
      setConfig({ configured: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleTest = async () => {
    if (!testTo.trim() || !testTo.includes("@")) {
      toast.error("Enter a valid test recipient email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/email/test", { to: testTo.trim() });
      if (data.ok) {
        toast.success(`Test email sent to ${testTo.trim()}`);
      } else {
        toast.error(data.error || "SMTP test failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SMTP test failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bento-card lg:col-span-2" data-testid="smtp-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-[#312E81]/10 rounded-md">
          <Mail size={20} className="text-[#312E81]" />
        </div>
        <h2
          className="text-xl font-medium text-[#1C1917]"
          style={{ fontFamily: "Playfair Display, serif" }}
        >
          Outbound Email (SMTP)
        </h2>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#57534E]">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : !config?.configured ? (
        <div className="flex items-start gap-2 p-3 rounded-md bg-[#FEF2F2] text-sm text-[#991B1B]" data-testid="smtp-not-configured">
          <XCircle size={16} className="mt-0.5 shrink-0" />
          <div>
            SMTP is not configured. Set <code>SMTP_HOST</code>, <code>SMTP_PORT</code>, <code>SMTP_USERNAME</code>,{" "}
            <code>SMTP_PASSWORD</code>, and <code>SMTP_FROM_EMAIL</code> in the backend <code>.env</code>, then restart the backend.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-[#15803D]" data-testid="smtp-configured">
              <CheckCircle size={16} /> SMTP configured
            </div>
            <dl className="text-sm grid grid-cols-[max-content,1fr] gap-x-4 gap-y-1">
              <dt className="text-[#57534E]">Host</dt>
              <dd className="font-mono text-[#1C1917]" data-testid="smtp-host">{config.host}</dd>
              <dt className="text-[#57534E]">Port</dt>
              <dd className="font-mono text-[#1C1917]" data-testid="smtp-port">
                {config.port} {config.use_tls ? "(TLS)" : "(STARTTLS)"}
              </dd>
              <dt className="text-[#57534E]">From</dt>
              <dd className="text-[#1C1917]" data-testid="smtp-from">
                {config.from_name ? `${config.from_name} <${config.from_email}>` : config.from_email}
              </dd>
              <dt className="text-[#57534E]">User</dt>
              <dd className="font-mono text-[#1C1917]" data-testid="smtp-user">{config.username}</dd>
            </dl>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-[#1C1917]">Send a test email</label>
            <input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="recipient@example.com"
              className="w-full px-3 py-2 text-sm border border-[#E7E5E4] rounded-md focus:outline-none focus:ring-2 focus:ring-[#312E81]/30"
              data-testid="smtp-test-to-input"
            />
            <button
              onClick={handleTest}
              disabled={sending || !testTo.trim()}
              data-testid="smtp-send-test-btn"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-[#312E81] text-white hover:bg-[#25215E] disabled:opacity-60 transition-colors"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send test email
            </button>
            <p className="text-xs text-[#78716C]">
              A short confirmation email will be sent from {config.from_email}.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
